import type { FilterChip, Lookups } from "../../types";
import { renderFilterDetailList } from "../../components/FilterDetailList";
import { mapIdsToNames } from "../../utils/mapIdsToNames";
import type { GeographySlice } from "./slice";

export function getChips(
  slice: GeographySlice,
  setSlice: (slice: GeographySlice) => void,
  lookups?: Lookups
): FilterChip[] {
  const chips: FilterChip[] = [];

  const regionsCount = slice.regions.length;
  if (regionsCount > 0) {
    // Generate region label based on selected regions
    let regionLabel: string;
    if (regionsCount === 1) {
      const [onlyRegion] = slice.regions;
      regionLabel = onlyRegion;
    } else {
      const sortedRegions = [...slice.regions].sort();
      const lastRegion = sortedRegions[sortedRegions.length - 1];
      const otherRegions = sortedRegions.slice(0, -1);
      regionLabel = `${otherRegions.join(", ")} or ${lastRegion}`;
    }

    chips.push({
      id: "regions",
      label: `Regions: ${regionLabel}`,
      filterKey: "geography" as const,
      onRemove: () => setSlice({ ...slice, regions: [] }),
      // No tooltip for Regions (only 3 values)
    });
  }

  const countriesCount = slice.countries.length;
  const statesCount = slice.states.length;
  if (countriesCount > 0) {
    // Get country names for smart label
    const countryNames = lookups?.countries
      ? mapIdsToNames(slice.countries, lookups.countries)
      : slice.countries;
    // Get state names for smart label
    const stateNames = lookups?.states
      ? mapIdsToNames(slice.states, lookups.states)
      : slice.states;

    // Build smart label
    let label: string;
    if (countriesCount === 1) {
      const countryName = countryNames[0] ?? slice.countries[0];
      if (statesCount === 1) {
        label = `Countries: ${countryName} (${stateNames[0]})`;
      } else if (statesCount > 1) {
        label = `Countries: ${countryName} (${statesCount} states)`;
      } else {
        label = `Countries: ${countryName}`;
      }
    } else {
      const statesLabel =
        statesCount > 0
          ? ` (${statesCount} state${statesCount > 1 ? "s" : ""})`
          : "";
      label = `Countries: ${countriesCount} selected${statesLabel}`;
    }

    // Build tooltip for countries and states
    const tooltipContent = lookups ? (
      <>
        {slice.countries.length > 0 &&
          lookups.countries &&
          renderFilterDetailList("Countries", countryNames)}
        {slice.states.length > 0 &&
          lookups.states &&
          renderFilterDetailList("States", stateNames)}
      </>
    ) : undefined;

    chips.push({
      id: "countries",
      label,
      filterKey: "geography" as const,
      onRemove: () => setSlice({ ...slice, countries: [], states: [] }),
      tooltipContent,
    });
  }

  const excludedCount = slice.excludedCountries.length;
  if (excludedCount > 0) {
    // Get excluded country names for smart label
    const excludedNames = lookups?.countries
      ? mapIdsToNames(slice.excludedCountries, lookups.countries)
      : slice.excludedCountries;

    const label =
      excludedCount === 1
        ? `Excluded Countries: ${excludedNames[0] ?? slice.excludedCountries[0]}`
        : `Excluded Countries: ${excludedCount} selected`;

    // Build tooltip for excluded countries
    const tooltipContent =
      lookups?.countries && slice.excludedCountries.length > 0
        ? renderFilterDetailList("Countries", excludedNames)
        : undefined;

    chips.push({
      id: "excludedCountries",
      label,
      filterKey: "geography" as const,
      onRemove: () => setSlice({ ...slice, excludedCountries: [] }),
      tooltipContent,
    });
  }

  return chips;
}

export function getCount(slice: GeographySlice): number {
  return (
    slice.countries.length +
    slice.regions.length +
    slice.excludedCountries.length +
    slice.states.length
  );
}
